/**
 * Generates a thumbnail image from a video file
 * @param videoFile - The video file blob
 * @param seekTime - Time in seconds to capture the frame (default: 1)
 * @returns Promise<Blob> - The thumbnail image as a JPEG blob
 */
export async function generateVideoThumbnail(
  videoFile: Blob,
  seekTime: number = 1
): Promise<Blob> {
  return new Promise((resolve, reject) => {
    try {
      const video = document.createElement('video');
      const canvas = document.createElement('canvas');
      const context = canvas.getContext('2d');

      if (!context) {
        reject(new Error('Could not get canvas context'));
        return;
      }

      video.preload = 'metadata';
      video.muted = true;
      video.playsInline = true; // Important for iOS

      // Create object URL from blob
      const videoUrl = URL.createObjectURL(videoFile);
      video.src = videoUrl;

      video.onloadedmetadata = () => {
        // Set canvas size to video size (or scale down for smaller thumbnails)
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;

        // Seek to the specified time
        video.currentTime = Math.min(seekTime, video.duration);
      };

      video.onseeked = () => {
        try {
          // Draw the current video frame to canvas
          context.drawImage(video, 0, 0, canvas.width, canvas.height);

          // Convert canvas to blob
          canvas.toBlob(
            (blob) => {
              // Clean up
              URL.revokeObjectURL(videoUrl);
              video.remove();
              canvas.remove();

              if (blob) {
                resolve(blob);
              } else {
                reject(new Error('Failed to create thumbnail blob'));
              }
            },
            'image/jpeg',
            0.8 // Quality (0.8 = 80%)
          );
        } catch (error) {
          URL.revokeObjectURL(videoUrl);
          reject(error);
        }
      };

      video.onerror = () => {
        URL.revokeObjectURL(videoUrl);
        reject(new Error('Error loading video'));
      };

      // Start loading the video
      video.load();
    } catch (error) {
      reject(error);
    }
  });
}

